<script setup lang="ts">
  import dayjs from 'dayjs';
  import { computed, onBeforeMount, ref } from 'vue';
  import type { HTMLAttributes } from 'vue';

  import type { Sms, SMSThread } from '@stuut/api-sdk/models';
  import { SMSStatus } from '@stuut/api-sdk/models';
  import type { SMSJSON } from '@stuut/api/models';
  import { formatPhoneNumber } from '@stuut/common/src/utils';
  import { Badge, DirectionBadge } from '@ui/components/badge';
  import { Button } from '@ui/components/button';
  import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@ui/components/collapsible';
  import { Skeleton } from '@ui/components/skeleton';
  import { Timestamp } from '@ui/components/time';

  import { SMSDrawer } from '@/components/sms';
  import { sdk } from '@/sdk';

  interface Props {
    containerClass?: HTMLAttributes['class'];
    hideCollapsibleTrigger?: boolean;
    sms?: SMSJSON;
  }

  const props = defineProps<Props>();

  const isExpanded = ref(true);
  const messages = ref<Sms[]>([]);
  const thread = ref<SMSThread>();
  const loading = ref(false);
  const showSmsDrawer = ref(false);

  const fromNumberFormatted = computed(() =>
    props.sms?.from_number ? formatPhoneNumber(props.sms.from_number) : '',
  );
  const toNumberFormatted = computed(() =>
    props.sms?.to_number ? formatPhoneNumber(props.sms.to_number) : '',
  );

  const fetchMessages = async () => {
    if (!props.sms?.sms_thread_id) return;

    loading.value = true;
    try {
      const response = await sdk.smsThreads.listMessages({
        smsThreadId: props.sms.sms_thread_id,
      });
      messages.value = response || [];
    } catch (err) {
      console.error('Error fetching SMS messages:', err);
    } finally {
      loading.value = false;
    }
  };

  const fetchThread = async () => {
    if (!props.sms?.sms_thread_id) return;

    try {
      thread.value = await sdk.smsThreads.get({
        smsThreadId: props.sms.sms_thread_id,
      });
    } catch (err) {
      console.error('Error fetching SMS thread:', err);
    }
  };

  const openSmsDrawer = async () => {
    if (!thread.value) {
      await fetchThread();
    }
    showSmsDrawer.value = true;
  };

  onBeforeMount(async () => {
    await fetchMessages();
  });
</script>

<template>
  <Collapsible v-if="sms" v-model:open="isExpanded">
    <div class="space-y-4">
      <div class="flex items-center justify-between gap-x-2.5">
        <CollapsibleTrigger v-if="!hideCollapsibleTrigger">
          <div class="flex items-center cursor-pointer text-muted-foreground gap-x-2.5">
            <i class="far fa-angle-right transition-all" :class="isExpanded && 'fa-rotate-90'" />
            <span>SMS Thread ({{ messages.length }})</span>
          </div>
        </CollapsibleTrigger>

        <Button
          v-if="sms?.sms_thread_id"
          class="font-bold"
          size="inline"
          variant="link"
          @click="openSmsDrawer"
        >
          View full thread
        </Button>
      </div>

      <CollapsibleContent>
        <div class="space-y-4" :class="containerClass">
          <div class="rounded p-4 border bg-background space-y-4">
            <!-- Thread Info -->
            <div class="flex items-center justify-between text-xs text-muted-foreground">
              <div class="flex items-center gap-x-2">
                <span
                  >From: <strong class="text-foreground">{{ fromNumberFormatted }}</strong></span
                >
                <span>→</span>
                <span
                  >To: <strong class="text-foreground">{{ toNumberFormatted }}</strong></span
                >
              </div>
            </div>

            <!-- Loading State -->
            <div v-if="loading" class="space-y-3">
              <div class="flex flex-col p-3 rounded-lg ml-0 mr-20">
                <Skeleton class="w-16 h-5 mb-2" />
                <Skeleton class="w-full h-4 mb-1" />
                <Skeleton class="w-3/4 h-4 mb-2" />
                <Skeleton class="w-24 h-3" />
              </div>
            </div>

            <!-- Messages -->
            <div v-else-if="messages.length" class="space-y-3 max-h-[400px] overflow-y-auto">
              <div
                v-for="message in messages"
                :key="message.id"
                class="flex flex-col p-3 rounded-lg"
                :class="[
                  message.direction === 'inbound'
                    ? 'ml-0 mr-16 items-start'
                    : 'bg-primary/10 ml-16 mr-0 items-end',
                ]"
              >
                <div
                  class="flex mb-2"
                  :class="[message.direction === 'outbound' ? 'justify-end' : 'justify-start']"
                >
                  <DirectionBadge
                    :direction="message.direction === 'inbound' ? 'inbound' : 'outbound'"
                    size="xs"
                    with-label
                  />
                  <Badge
                    v-if="
                      message.status === SMSStatus.Failed ||
                      message.status === SMSStatus.Undelivered
                    "
                    variant="destructive"
                    size="xs"
                  >
                    Failed
                  </Badge>
                </div>

                <div class="text-sm whitespace-pre-wrap">
                  {{ message.body }}
                </div>

                <Timestamp
                  class="mt-2 text-xs text-muted-foreground"
                  :time="dayjs(message.sentAt || message.createdAt).utc(true)"
                  with-conversion-table
                  local
                  show-from-now
                />
              </div>
            </div>

            <!-- No Messages -->
            <div v-else class="text-center py-4 text-muted-foreground text-sm">
              No messages found
            </div>
          </div>
        </div>
      </CollapsibleContent>

      <SMSDrawer
        v-if="thread"
        v-model:open="showSmsDrawer"
        :thread="thread"
        @close="showSmsDrawer = false"
      />
    </div>
  </Collapsible>
</template>
